/**
 * Post Editor — category-centric design studio.
 *
 * Flow:
 *   1. Admin creates CATEGORIES (sidebar). Each category stores its OWN
 *      backgrounds, photos and logos.
 *   2. Picks a category → the asset grids and uploads become scoped to
 *      that category only.
 *   3. Picks a platform tab → composes bg + photo + text layers; the
 *      category's logo auto-attaches.
 *   4. Composed PNG/JPEG lands in the library on the right.
 *
 * Supporting modules (see `./post-editor/`):
 *   - constants.js       : fonts, palette, defaults, autoLayout helpers.
 *   - CanvasLayers.jsx   : DraggableTextLayer + DraggablePhoto + snap logic.
 *   - InlineToolbar.jsx  : floating per-layer toolbar (portal-rendered) +
 *                          ColorPopover.
 *   - LayerPanel.jsx     : right-rail layers list with vis/lock/z-order.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Palette, Upload, Image as ImageIcon, User, Sparkles, Trash2,
  Loader2, Save, Download, Pencil, FolderTree, Hash, Wand2, Copy,
  X, ChevronRight, Layout, Bookmark, FolderOpen, Plus, Check,
  AlignLeft, AlignCenter, AlignRight,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  LOGO_POSITIONS,
  FONT_FAMILIES, FONT_CSS_MAP, FONT_LABEL_MAP,
  PALETTE_GROUPS, PALETTE_FLAT,
  readRecentColors, pushRecentColor,
  DEFAULT_PHOTO, makeLayerId,
  AUTO_SIZE_LADDER, AUTO_COLOR_LADDER, AUTO_START_Y, AUTO_LINE_FACTOR,
  MW_WITH_PHOTO, MW_NO_PHOTO,
  autoSize, autoY, autoLayout, freshLayer, INITIAL_LAYERS, absUrl,
} from "./post-editor/constants";
import { DraggableTextLayer, DraggablePhoto } from "./post-editor/CanvasLayers";
import { InlineToolbar } from "./post-editor/InlineToolbar";
import { LayerPanel } from "./post-editor/LayerPanel";
import { DraftsCard } from "./post-editor/DraftsCard";

// --------------------------------------------------------------------------
// Category sidebar — list + create + rename + delete
// --------------------------------------------------------------------------
function CategorySidebar({ categories, activeId, onPick, onRefresh }) {
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/post-editor/categories", { name });
      setNewName("");
      await onRefresh();
      onPick(data.id);
      toast.success(`"${name}" kategorisi oluşturuldu`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const saveRename = async (cid) => {
    const name = editName.trim();
    if (!name) { setEditingId(null); return; }
    try {
      await api.put(`/admin/post-editor/categories/${cid}`, { name });
      setEditingId(null);
      await onRefresh();
      toast.success("Yeniden adlandırıldı");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (c) => {
    const total = (c.counts?.backgrounds || 0) + (c.counts?.photos || 0) + (c.counts?.logos || 0);
    const msg = total > 0
      ? `"${c.name}" kategorisi ve içindeki ${total} varlık silinecek. Emin misin?`
      : `"${c.name}" kategorisi silinsin mi?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/post-editor/categories/${c.id}`);
      await onRefresh();
      if (activeId === c.id) onPick("");
      toast.success("Kategori silindi");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <aside className="bg-white rounded-xl border border-indigo-100 shadow-sm p-3 flex flex-col gap-2 min-w-0"
           data-testid="pe-category-sidebar">
      <div className="flex items-center gap-1.5">
        <FolderTree size={14} className="text-indigo-600" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-900 flex-1">Kategoriler</h3>
      </div>

      <div className="flex gap-1.5">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)}
               placeholder="Yeni kategori adı"
               className="h-8 text-xs"
               data-testid="pe-category-new"
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }} />
        <Button size="sm" onClick={create} disabled={busy || !newName.trim()} data-testid="pe-category-create"
                title="Yeni kategori oluştur">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={12} />}
        </Button>
      </div>

      {categories.length === 0 ? (
        <p className="text-[11px] text-stone-400 italic text-center py-6">
          Henüz kategori yok. Yukarıdaki alandan oluştur. Örn: "Konuşmadan Olmaz".
        </p>
      ) : (
        <ul className="space-y-1 overflow-y-auto max-h-[520px]" data-testid="pe-category-list">
          {categories.map((c) => {
            const total = (c.counts?.backgrounds || 0) + (c.counts?.photos || 0) + (c.counts?.logos || 0);
            const selected = activeId === c.id;
            if (editingId === c.id) {
              return (
                <li key={c.id} className="flex gap-1">
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                         className="h-8 text-xs"
                         autoFocus
                         onKeyDown={(e) => {
                           if (e.key === "Enter") { e.preventDefault(); saveRename(c.id); }
                           if (e.key === "Escape") { setEditingId(null); }
                         }} />
                  <Button size="sm" onClick={() => saveRename(c.id)}><Check size={11} /></Button>
                </li>
              );
            }
            return (
              <li key={c.id}>
                <div className={`group flex items-center gap-1.5 p-2 rounded-lg border transition cursor-pointer ${
                  selected ? "bg-indigo-50 border-indigo-300 shadow-sm" : "bg-stone-50 border-stone-200 hover:border-indigo-300"
                }`} onClick={() => onPick(c.id)} data-testid={`pe-category-${c.id}`}>
                  <FolderOpen size={12} className={selected ? "text-indigo-600" : "text-stone-500"} />
                  <span className={`flex-1 text-xs truncate ${selected ? "font-bold text-indigo-900" : "text-slate-800"}`}>
                    {c.name}
                  </span>
                  {total > 0 && (
                    <span className="text-[9px] bg-stone-200 text-stone-700 rounded-full px-1.5">{total}</span>
                  )}
                  <button type="button"
                          onClick={(e) => { e.stopPropagation(); setEditingId(c.id); setEditName(c.name); }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-white"
                          title="Yeniden adlandır"
                          data-testid={`pe-category-rename-${c.id}`}>
                    <Pencil size={10} className="text-stone-500" />
                  </button>
                  <button type="button"
                          onClick={(e) => { e.stopPropagation(); remove(c); }}
                          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-50"
                          title="Sil"
                          data-testid={`pe-category-delete-${c.id}`}>
                    <Trash2 size={10} className="text-red-500" />
                  </button>
                </div>
                {selected && total > 0 && (
                  <div className="pl-5 mt-1 flex gap-2 text-[10px] text-stone-500">
                    <span>{c.counts?.backgrounds || 0} arka plan</span>
                    <span>{c.counts?.photos || 0} foto</span>
                    <span>{c.counts?.logos || 0} logo</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <p className="text-[10px] text-stone-400 mt-auto pt-2 border-t">
        Her kategori kendi arka plan, foto ve logolarını barındırır. Önce bir kategori seç; yüklemeler ve seçim listeleri o kategoriye kilitlenir.
      </p>
    </aside>
  );
}

// -- Upload tile (now category-aware) --------------------------------------
function UploadTile({ kind, categoryId, extra = {}, onDone, label, disabled }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category_id", categoryId);
    for (const [k, v] of Object.entries(extra)) if (v != null) fd.append(k, String(v));
    try {
      await api.post(`/admin/post-editor/${kind}/upload`, fd);
      toast.success(`${label} yüklendi`);
      onDone?.();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div
      className={`border-2 border-dashed rounded-xl p-3 text-center transition flex flex-col items-center justify-center min-h-[90px] ${
        disabled
          ? "border-stone-200 bg-stone-50 opacity-50 cursor-not-allowed"
          : "border-indigo-200 cursor-pointer hover:bg-indigo-50/40"
      }`}
      onClick={() => !disabled && inputRef.current?.click()}
      data-testid={`pe-upload-${kind}`}
      title={disabled ? "Önce bir kategori seç" : ""}
    >
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pick} />
      {busy ? <Loader2 size={18} className="animate-spin text-indigo-600" /> : <Upload size={18} className="text-indigo-500" />}
      <span className="text-[11px] text-indigo-700 mt-1 font-semibold">{label}</span>
      <span className="text-[10px] text-stone-400">PNG · JPEG · WebP · GIF — 10 MB'a kadar</span>
    </div>
  );
}

// -- Asset grid (bg / photo / logo) ----------------------------------------
function AssetGrid({ rows, selectedId, onPick, onDelete, kind, testid }) {
  if (!rows.length) return (
    <p className="text-xs text-stone-400 italic text-center py-4">
      Henüz {kind === "backgrounds" ? "arka plan" : kind === "photos" ? "fotoğraf" : "logo"} yok.
    </p>
  );
  return (
    <ul className="grid grid-cols-3 gap-1.5" data-testid={testid}>
      {rows.map((r) => {
        const selected = selectedId === r.id;
        return (
          <li key={r.id} className="group relative">
            <button
              type="button"
              onClick={() => onPick(r)}
              className={`block w-full aspect-square rounded-lg border-2 overflow-hidden bg-stone-50 ${
                selected ? "border-indigo-500 ring-2 ring-indigo-200" : "border-stone-200 hover:border-indigo-300"
              }`}
              title={r.name}
              data-testid={`pe-asset-${kind}-${r.id}`}
            >
              <img src={absUrl(r.url)} alt={r.name} className="w-full h-full object-cover" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(r)}
              className="absolute top-0.5 right-0.5 p-1 rounded bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600"
              title="Sil"
            >
              <Trash2 size={10} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// -- Text generation modal --------------------------------------------------
function TextGenModal({ open, onClose, platform, onApply }) {
  const [tab, setTab] = useState("writing");
  const [writingId, setWritingId] = useState("");
  const [source, setSource] = useState("");
  const [tone, setTone] = useState("natural");
  const [providerId, setProviderId] = useState("");
  const [wantHashtags, setWantHashtags] = useState(true);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState({ caption: "", hashtags: [] });

  const { data: writings = [] } = useQuery({
    queryKey: ["workspace", "writings", "picker"],
    queryFn: () => api.get("/admin/workspace/writings").then((r) => r.data),
    enabled: open,
  });
  const { data: providers = [] } = useQuery({
    queryKey: ["nova", "providers"],
    queryFn: () => api.get("/admin/nova/providers").then((r) => r.data),
    enabled: open,
  });

  useEffect(() => {
    if (open && providers.length && !providerId) {
      setProviderId((providers.find((p) => p.is_default) || providers[0])?.id || "");
    }
  }, [open, providers, providerId]);

  if (!open) return null;

  const run = async () => {
    const body = {
      platform, tone, want_hashtags: wantHashtags,
      provider_id: providerId || null,
      source_text: tab === "manual" ? source : "",
      source_writing_id: tab === "writing" ? (writingId || null) : null,
    };
    if (!body.source_text && !body.source_writing_id) {
      toast.error("Kaynak yazı seç veya kaynak metni yapıştır");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/post-editor/text", body);
      setOut({ caption: data.caption || "", hashtags: data.hashtags || [] });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[90] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="pe-text-modal">
        <div className="px-5 py-3 border-b bg-gradient-to-r from-amber-50 to-rose-50 flex items-center gap-2">
          <Wand2 size={18} className="text-amber-600" />
          <h3 className="font-semibold text-slate-900 flex-1">Metin & Hashtag Üret</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/50 rounded"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="flex gap-1 bg-stone-100 p-1 rounded-lg">
            {[["writing", "Workspace yazısı"], ["manual", "Serbest metin"]].map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)}
                      className={`flex-1 py-1.5 rounded text-xs font-semibold ${tab === v ? "bg-white shadow text-indigo-700" : "text-stone-500 hover:text-stone-800"}`}
                      data-testid={`pe-text-tab-${v}`}>
                {l}
              </button>
            ))}
          </div>
          {tab === "writing" ? (
            <select value={writingId} onChange={(e) => setWritingId(e.target.value)}
                    className="w-full h-9 border rounded-md px-3 text-sm bg-white"
                    data-testid="pe-text-writing-pick">
              <option value="">— Yazı seç —</option>
              {writings.map((w) => <option key={w.id} value={w.id}>{w.title || "(başlıksız)"}</option>)}
            </select>
          ) : (
            <Textarea value={source} onChange={(e) => setSource(e.target.value)} rows={6}
                      placeholder="Kaynak metni buraya yapıştır..." data-testid="pe-text-source" />
          )}

          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-semibold text-stone-600 block mb-1">Ton</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)}
                      className="w-full h-9 border rounded-md px-3 text-sm bg-white"
                      data-testid="pe-text-tone">
                <option value="natural">Doğal</option>
                <option value="energetic">Enerjik</option>
                <option value="formal">Resmi</option>
                <option value="witty">Esprili</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-stone-600 block mb-1">Model</label>
              <select value={providerId} onChange={(e) => setProviderId(e.target.value)}
                      className="w-full h-9 border rounded-md px-3 text-sm bg-white"
                      data-testid="pe-text-provider">
                {providers.length === 0 && <option value="">— Önce NovaAI sağlayıcı ekle —</option>}
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} {p.is_default ? "★" : ""}</option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={wantHashtags} onChange={(e) => setWantHashtags(e.target.checked)}
                   data-testid="pe-text-hashtag-toggle" />
            Hashtag listesi de üret
          </label>

          <Button onClick={run} disabled={busy} className="w-full" data-testid="pe-text-run">
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Sparkles size={14} className="mr-2" />}
            Üret
          </Button>

          {(out.caption || out.hashtags.length > 0) && (
            <div className="border rounded-lg p-3 bg-amber-50/50 space-y-2" data-testid="pe-text-output">
              <div>
                <label className="text-[11px] font-semibold text-amber-800">Metin</label>
                <Textarea value={out.caption} onChange={(e) => setOut({ ...out, caption: e.target.value })}
                          rows={5} className="text-sm" />
              </div>
              {out.hashtags.length > 0 && (
                <div>
                  <label className="text-[11px] font-semibold text-amber-800">Hashtag ({out.hashtags.length})</label>
                  <Input value={out.hashtags.join(" ")} onChange={(e) => setOut({ ...out, hashtags: e.target.value.split(/\s+/).filter(Boolean) })} />
                </div>
              )}
              <Button
                onClick={() => { onApply(out); onClose(); }}
                size="sm"
                className="w-full"
                data-testid="pe-text-apply"
              >
                <ChevronRight size={13} className="mr-1" />Kompozisyona uygula
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// -- Templates panel --------------------------------------------------------
function TextLayersEditor({ layers, setLayers, activeId, setActiveId, hasPhoto = false, studioOnly = false }) {
  // In non-studio mode we hide the detailed per-row editor entirely so the
  // canvas preview isn't competing with a tall list of inputs. Users add &
  // edit text directly on the canvas inside Stüdyo Modu (click-to-add + the
  // floating InlineToolbar).
  if (studioOnly) {
    return (
      <div className="rounded-lg border border-dashed border-indigo-200 bg-indigo-50/50 p-3 text-[11px] text-indigo-900"
           data-testid="pe-text-studio-hint">
        <span className="font-semibold">Metin eklemek için Stüdyo Modunu aç</span> ·
        <span className="opacity-75"> Tuval üzerinde istediğin yere tıkla, metni yaz, renk/boyut InlineToolbar'dan ayarlanır.</span>
      </div>
    );
  }
  const addLayer = () => {
    const nl = freshLayer({}, layers.length, hasPhoto);
    setLayers([...layers, nl]);
    setActiveId(nl.id);
  };
  const updateLayer = (id, patch) => setLayers(layers.map((l) => l.id === id ? { ...l, ...patch } : l));
  const removeLayer = (id) => {
    const kept = layers.filter((l) => l.id !== id);
    const next = kept.map((l, i) => ({
      ...l,
      ...autoLayout(i, hasPhoto),
      text: l.text,
      // Preserve manual max_width override
      max_width: l._mwAuto === false ? l.max_width : (hasPhoto ? MW_WITH_PHOTO : MW_NO_PHOTO),
    }));
    setLayers(next.length ? next : [freshLayer({}, 0, hasPhoto)]);
    if (activeId === id) setActiveId(null);
  };
  const moveLayer = (id, dir) => {
    const idx = layers.findIndex((l) => l.id === id);
    const to = idx + dir;
    if (idx < 0 || to < 0 || to >= layers.length) return;
    const next = layers.slice();
    [next[idx], next[to]] = [next[to], next[idx]];
    const reflowed = next.map((l, i) => ({
      ...l,
      ...autoLayout(i, hasPhoto),
      text: l.text,
      color: l.color || autoLayout(i, hasPhoto).color,
      max_width: l._mwAuto === false ? l.max_width : (hasPhoto ? MW_WITH_PHOTO : MW_NO_PHOTO),
    }));
    setLayers(reflowed);
  };
  const reflowAll = () => {
    const next = layers.map((l, i) => ({
      ...l,
      ...autoLayout(i, hasPhoto),
      text: l.text,
      _mwAuto: true,
    }));
    setLayers(next);
    toast.success("Oto yerleşim uygulandı");
  };

  return (
    <div className="space-y-2" data-testid="pe-text-layers">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold text-stone-600 flex-1">
          Metin Katmanları <span className="text-stone-400 font-normal">({layers.length})</span>
        </span>
        <button type="button" onClick={reflowAll}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-semibold hover:bg-emerald-100"
                title="Tüm satırları standart yerleşime (sol-üst, alt alta) döndür"
                data-testid="pe-layer-reflow">
          ⥀ Oto yerleş
        </button>
        <button type="button" onClick={addLayer}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 text-[11px] font-semibold hover:bg-indigo-100"
                data-testid="pe-layer-add">
          <Plus size={11} /> Satır ekle
        </button>
      </div>
      {layers.map((l, idx) => {
        const open = activeId === l.id;
        return (
          <div key={l.id}
               className={`border rounded-lg overflow-hidden transition ${open ? "border-indigo-400 shadow-sm" : "border-stone-200"}`}
               data-testid={`pe-layer-${idx}`}>
            <div className="flex items-center gap-1 p-1.5 bg-stone-50">
              <button type="button" onClick={() => setActiveId(open ? null : l.id)}
                      className="text-[10px] font-mono text-stone-500 px-1 hover:text-indigo-700"
                      title={open ? "Daralt" : "Düzenle"}
                      data-testid={`pe-layer-toggle-${idx}`}>
                #{idx + 1}
              </button>
              <Input value={l.text}
                     onChange={(e) => updateLayer(l.id, { text: e.target.value })}
                     onFocus={() => setActiveId(l.id)}
                     placeholder={idx === 0 ? "Ana satır" : `Satır ${idx + 1}`}
                     className="h-7 text-xs flex-1"
                     data-testid={`pe-layer-text-${idx}`} />
              <input type="color" value={l.color}
                     onChange={(e) => updateLayer(l.id, { color: e.target.value })}
                     className="h-7 w-7 border rounded cursor-pointer shrink-0"
                     title="Renk"
                     data-testid={`pe-layer-color-${idx}`} />
              <button type="button" onClick={() => moveLayer(l.id, -1)}
                      disabled={idx === 0}
                      className="text-stone-400 hover:text-indigo-700 disabled:opacity-30 px-1"
                      title="Yukarı" data-testid={`pe-layer-up-${idx}`}>▲</button>
              <button type="button" onClick={() => moveLayer(l.id, 1)}
                      disabled={idx === layers.length - 1}
                      className="text-stone-400 hover:text-indigo-700 disabled:opacity-30 px-1"
                      title="Aşağı" data-testid={`pe-layer-down-${idx}`}>▼</button>
              <button type="button" onClick={() => removeLayer(l.id)}
                      className="text-red-500 hover:bg-red-50 rounded p-1"
                      title="Sil" data-testid={`pe-layer-del-${idx}`}>
                <Trash2 size={11} />
              </button>
            </div>
            {open && (
              <div className="p-2 space-y-2 bg-white">
                <div className="flex items-center gap-1 flex-wrap">
                  {PALETTE_FLAT.slice(0, 24).map((c) => (
                    <button key={c} type="button"
                            onClick={() => { updateLayer(l.id, { color: c }); pushRecentColor(c); }}
                            className={`w-5 h-5 rounded-full border ${l.color?.toLowerCase() === c.toLowerCase() ? "ring-2 ring-indigo-500" : "border-stone-300"}`}
                            style={{ backgroundColor: c }}
                            title={c}
                            data-testid={`pe-layer-swatch-${idx}-${c.slice(1)}`} />
                  ))}
                </div>
                <details className="rounded-md border border-stone-200" data-testid={`pe-layer-pro-${idx}`}>
                  <summary className="px-2 py-1 bg-stone-50 text-[10px] font-semibold text-stone-600 cursor-pointer select-none">
                    ⚙️ Pro ayarlar (manuel konum / boyut / font)
                  </summary>
                  <div className="p-2 space-y-2 text-[11px]">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <select value={l.font_family || "inter"}
                              onChange={(e) => updateLayer(l.id, { font_family: e.target.value })}
                              className="h-7 text-[11px] border rounded px-1 bg-white"
                              style={{ fontFamily: FONT_CSS_MAP[l.font_family || "inter"] }}
                              data-testid={`pe-layer-font-${idx}`}>
                        {FONT_FAMILIES.map(([v, lb]) => (
                          <option key={v} value={v} style={{ fontFamily: FONT_CSS_MAP[v] }}>{lb}</option>
                        ))}
                      </select>
                      <div className="flex gap-0.5">
                        <button type="button" onClick={() => updateLayer(l.id, { bold: !l.bold })}
                                className={`h-7 w-7 text-[11px] border rounded font-bold ${l.bold ? "bg-slate-900 text-white border-slate-900" : "bg-white border-stone-200"}`}
                                data-testid={`pe-layer-bold-${idx}`}
                                title="Bold">B</button>
                        <button type="button" onClick={() => updateLayer(l.id, { italic: !l.italic })}
                                className={`h-7 w-7 text-[11px] border rounded ${l.italic ? "bg-slate-900 text-white border-slate-900" : "bg-white border-stone-200"}`}
                                style={{ fontStyle: "italic", fontWeight: 700 }}
                                data-testid={`pe-layer-italic-${idx}`}
                                title="Italic">I</button>
                        <button type="button" onClick={() => updateLayer(l.id, { underline: !l.underline })}
                                className={`h-7 w-7 text-[11px] border rounded ${l.underline ? "bg-slate-900 text-white border-slate-900" : "bg-white border-stone-200"}`}
                                style={{ textDecoration: "underline", fontWeight: 700 }}
                                data-testid={`pe-layer-underline-${idx}`}
                                title="Underline">U</button>
                      </div>
                      <select value={l.align}
                              onChange={(e) => updateLayer(l.id, { align: e.target.value })}
                              className="h-7 text-[11px] border rounded px-1 bg-white"
                              data-testid={`pe-layer-align-${idx}`}>
                        <option value="left">⬅ Sol</option>
                        <option value="center">⬌ Orta</option>
                        <option value="right">Sağ ➡</option>
                      </select>
                      <label className="inline-flex items-center gap-1 text-[11px] text-stone-600">
                        <input type="checkbox" checked={!!l.shadow}
                               onChange={(e) => updateLayer(l.id, { shadow: e.target.checked })}
                               data-testid={`pe-layer-shadow-${idx}`} />
                        Gölge
                      </label>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5 text-[10px] text-stone-500">
                      <label>X: {Math.round(l.x * 100)}%
                        <input type="range" min="0" max="1" step="0.01" value={l.x}
                               onChange={(e) => updateLayer(l.id, { x: parseFloat(e.target.value) })}
                               className="w-full" data-testid={`pe-layer-x-${idx}`} />
                      </label>
                      <label>Y: {Math.round(l.y * 100)}%
                        <input type="range" min="0" max="1" step="0.01" value={l.y}
                               onChange={(e) => updateLayer(l.id, { y: parseFloat(e.target.value) })}
                               className="w-full" data-testid={`pe-layer-y-${idx}`} />
                      </label>
                      <label>Boy: {Math.round(l.size * 1000) / 10}
                        <input type="range" min="0.02" max="0.2" step="0.005" value={l.size}
                               onChange={(e) => updateLayer(l.id, { size: parseFloat(e.target.value) })}
                               className="w-full" data-testid={`pe-layer-size-${idx}`} />
                      </label>
                    </div>
                    <label className="block text-[10px] text-stone-500">
                      Maks. genişlik: {Math.round(l.max_width * 100)}% {l._mwAuto === false && <span className="text-amber-600">· manuel</span>}
                      <input type="range" min="0.2" max="1" step="0.02" value={l.max_width}
                             onChange={(e) => updateLayer(l.id, { max_width: parseFloat(e.target.value), _mwAuto: false })}
                             className="w-full" data-testid={`pe-layer-mw-${idx}`} />
                    </label>
                  </div>
                </details>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Templates panel
// ---------------------------------------------------------------------------
function AiTemplateDialog({ open, onClose, platformId, platform, onSaved }) {
  const [brief, setBrief] = useState("");
  const [variants, setVariants] = useState(3);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setBrief(""); setVariants(3); } }, [open]);
  if (!open) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/post-editor/templates/ai", {
        platform: platformId,
        brief: brief.trim(),
        variants,
        save: true,
      });
      toast.success(`${data.generated} AI taslak üretildi ve kaydedildi`);
      onSaved?.();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}
           data-testid="pe-ai-modal">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-2xl" aria-hidden>🤖</span>
          <div className="flex-1">
            <h3 className="font-black text-slate-900 text-lg leading-tight">AI ile Taslak Üret</h3>
            <p className="text-xs text-stone-500">
              Örneklerden öğrenir, {platform.label} için <strong>{variants}</strong> yeni şablon üretir.
            </p>
          </div>
        </div>
        <label className="block text-xs font-semibold text-stone-700 mb-1">Brief (opsiyonel)</label>
        <textarea value={brief} onChange={(e) => setBrief(e.target.value)}
                  placeholder="Örn: &quot;Haber tarzı, sade, foto solda, 2 satır başlık&quot; · boş bırakırsan çeşitli stillerde üretir."
                  rows={3} maxLength={500}
                  className="w-full border rounded-lg p-2 text-sm focus:outline-indigo-500 resize-none"
                  data-testid="pe-ai-brief" />
        <label className="block text-xs font-semibold text-stone-700 mt-3 mb-1">
          Varyant adedi: <span className="text-indigo-700 font-bold">{variants}</span>
        </label>
        <input type="range" min="1" max="5" value={variants} onChange={(e) => setVariants(parseInt(e.target.value, 10))}
               className="w-full" data-testid="pe-ai-variants" />
        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} disabled={busy} className="flex-1" data-testid="pe-ai-cancel">
            İptal
          </Button>
          <Button onClick={submit} disabled={busy} className="flex-1 bg-indigo-700 hover:bg-indigo-800"
                  data-testid="pe-ai-submit">
            {busy ? <><Loader2 size={14} className="animate-spin mr-1" /> Üretiliyor…</> : <>✨ Üret & Kaydet</>}
          </Button>
        </div>
        <p className="text-[10px] text-stone-400 mt-2 italic text-center">
          AI birkaç saniye sürebilir · taslaklar Taslaklarım listende görünür.
        </p>
      </div>
    </div>
  );
}

// -------- Hizalama / Dağıtım yardımcıları ---------------------------------
function alignSelection(layers, selectedIds, mode) {
  const sel = layers.filter((l) => selectedIds.includes(l.id));
  if (sel.length < 2 && !mode.startsWith("canvas-")) return layers;
  let targetX, targetY;
  if (mode === "left")    targetX = Math.min(...sel.map((l) => l.x));
  if (mode === "right")   targetX = Math.max(...sel.map((l) => l.x));
  if (mode === "hcenter") targetX = sel.reduce((a, l) => a + l.x, 0) / sel.length;
  if (mode === "top")     targetY = Math.min(...sel.map((l) => l.y));
  if (mode === "bottom")  targetY = Math.max(...sel.map((l) => l.y));
  if (mode === "vcenter") targetY = sel.reduce((a, l) => a + l.y, 0) / sel.length;
  if (mode === "canvas-hcenter") targetX = 0.5;
  if (mode === "canvas-vcenter") targetY = 0.5;
  return layers.map((l) => {
    if (!selectedIds.includes(l.id)) return l;
    const patch = {};
    if (targetX != null) patch.x = targetX;
    if (targetY != null) patch.y = targetY;
    return { ...l, ...patch };
  });
}

function distributeSelection(layers, selectedIds, axis) {
  const sel = layers.filter((l) => selectedIds.includes(l.id));
  if (sel.length < 3) return layers;
  const key = axis === "x" ? "x" : "y";
  const sorted = [...sel].sort((a, b) => a[key] - b[key]);
  const min = sorted[0][key], max = sorted[sorted.length - 1][key];
  const step = (max - min) / (sorted.length - 1);
  const mapById = Object.fromEntries(sorted.map((l, i) => [l.id, min + i * step]));
  return layers.map((l) => selectedIds.includes(l.id)
    ? { ...l, [key]: mapById[l.id] }
    : l);
}

function AlignToolbar({ selectedIds, setLayers, canvasRef }) {
  if (selectedIds.length < 2) return null;
  const canvas = canvasRef.current;
  if (!canvas) return null;
  const rect = canvas.getBoundingClientRect();
  const run = (mode) => setLayers((prev) => alignSelection(prev, selectedIds, mode));
  const dist = (axis) => setLayers((prev) => distributeSelection(prev, selectedIds, axis));
  return (
    <div style={{ position: "absolute", left: 8, top: 32, width: rect.width - 16, pointerEvents: "none", zIndex: 25 }}>
      <div className="inline-flex items-center gap-1 bg-white rounded-lg shadow-xl border border-amber-200 px-1.5 py-1 pointer-events-auto"
           data-testid="pe-align-toolbar"
           onPointerDown={(e) => e.stopPropagation()}>
        <span className="text-[10px] text-stone-500 pr-1 font-semibold">Hiza:</span>
        <button type="button" onClick={() => run("left")}    className="w-6 h-6 rounded hover:bg-stone-100" title="Sola hizala" data-testid="pe-align-left">⇤</button>
        <button type="button" onClick={() => run("hcenter")} className="w-6 h-6 rounded hover:bg-stone-100" title="Yatay orta" data-testid="pe-align-hcenter">⇔</button>
        <button type="button" onClick={() => run("right")}   className="w-6 h-6 rounded hover:bg-stone-100" title="Sağa hizala" data-testid="pe-align-right">⇥</button>
        <span className="w-px h-4 bg-stone-200 mx-1" />
        <button type="button" onClick={() => run("top")}     className="w-6 h-6 rounded hover:bg-stone-100" title="Üste hizala" data-testid="pe-align-top">⤒</button>
        <button type="button" onClick={() => run("vcenter")} className="w-6 h-6 rounded hover:bg-stone-100" title="Dikey orta" data-testid="pe-align-vcenter">⇕</button>
        <button type="button" onClick={() => run("bottom")}  className="w-6 h-6 rounded hover:bg-stone-100" title="Alta hizala" data-testid="pe-align-bottom">⤓</button>
        <span className="w-px h-4 bg-stone-200 mx-1" />
        <button type="button" onClick={() => dist("x")} disabled={selectedIds.length < 3}
                className="px-1.5 h-6 rounded hover:bg-stone-100 text-[10px] disabled:opacity-30"
                title="Yatay dağıt (3+)" data-testid="pe-distribute-x">⇿</button>
        <button type="button" onClick={() => dist("y")} disabled={selectedIds.length < 3}
                className="px-1.5 h-6 rounded hover:bg-stone-100 text-[10px] disabled:opacity-30"
                title="Dikey dağıt (3+)" data-testid="pe-distribute-y">⇅</button>
      </div>
    </div>
  );
}


function TemplatesCard({ platformId, platform, categoryId, getCurrent, applyTemplate }) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const storageKey = `pe:lastTpl:${platformId}:${categoryId || "_"}`;
  const [lastTplId, setLastTplId] = useState(() => {
    try { return localStorage.getItem(storageKey) || null; } catch { return null; }
  });
  useEffect(() => {
    try { setLastTplId(localStorage.getItem(storageKey) || null); } catch { /* ignore */ }
  }, [storageKey]);
  const rememberTpl = (id) => {
    try { localStorage.setItem(storageKey, id); } catch { /* ignore */ }
    setLastTplId(id);
  };

  const { data: templates = [] } = useQuery({
    queryKey: ["pe", "templates", platformId],
    queryFn: () => api.get("/admin/post-editor/templates", { params: { platform: platformId } }).then((r) => r.data),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["pe", "templates", platformId] });

  const lastTpl = templates.find((t) => t.id === lastTplId) || null;

  const save = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Şablon adı gerekli"); return; }
    setBusy(true);
    try {
      const body = { name, platform: platformId, ...getCurrent() };
      const { data } = await api.post("/admin/post-editor/templates", body);
      toast.success(`Şablon "${name}" kaydedildi`);
      setNewName("");
      invalidate();
      if (data?.id) rememberTpl(data.id);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const overwrite = async (tpl) => {
    if (!window.confirm(`"${tpl.name}" şablonu şu anki düzen ile güncellensin mi?`)) return;
    try {
      await api.put(`/admin/post-editor/templates/${tpl.id}`, {
        name: tpl.name, platform: tpl.platform, ...getCurrent(),
      });
      toast.success("Şablon güncellendi");
      invalidate();
      rememberTpl(tpl.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (tpl) => {
    if (!window.confirm(`"${tpl.name}" şablonu silinsin mi?`)) return;
    try {
      await api.delete(`/admin/post-editor/templates/${tpl.id}`);
      toast.success("Silindi");
      invalidate();
      if (lastTplId === tpl.id) {
        try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
        setLastTplId(null);
      }
    } catch (e) { toast.error(errMsg(e)); }
  };

  const apply = (tpl) => {
    applyTemplate(tpl);
    // Built-in preset'ler servera kayıtlı değil; last-used rozetine takılmasın.
    if (tpl && tpl.id && !tpl._builtIn) rememberTpl(tpl.id);
  };

  return (
    <section className="rounded-xl border-2 border-amber-200 shadow-md overflow-hidden"
             data-testid="pe-templates-card">
      {/* vivid header */}
      <div className="bg-gradient-to-r from-amber-100 via-rose-100 to-indigo-100 px-3 py-2 flex items-center gap-2 border-b border-amber-200">
        <span className="text-base" aria-hidden>⭐</span>
        <div className="flex-1">
          <h3 className="text-xs font-black uppercase tracking-wider text-indigo-900 leading-none">Taslaklarım</h3>
          <p className="text-[10px] text-stone-600 mt-0.5">
            Düzeni bir kez kur · satırları değiştirerek tekrar tekrar kullan
          </p>
        </div>
        <span className="text-[10px] bg-white/70 text-indigo-800 rounded-full px-2 py-0.5 font-semibold">
          {platform.label}
        </span>
      </div>

      <div className="p-3 bg-white space-y-2">
        {lastTpl && (
          <button type="button" onClick={() => apply(lastTpl)}
                  className="w-full text-left flex items-center gap-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg px-2 py-1.5 transition"
                  data-testid="pe-tpl-last-apply">
            <span className="text-xs">⟲</span>
            <span className="text-[11px] text-emerald-900 flex-1 truncate">
              <span className="font-semibold">Son kullanılan:</span> {lastTpl.name}
            </span>
            <span className="text-[9px] uppercase bg-white text-emerald-700 border border-emerald-200 rounded px-1 font-semibold">
              Yeniden uygula
            </span>
          </button>
        )}

        {/* Hazır preset galerisi kaldırıldı — artık yalnızca kullanıcı taslakları ve AI üretimi. */}

        <div className="flex gap-1.5">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                 placeholder={`${platform.label} taslağına ad ver…`} className="h-8 text-xs"
                 data-testid="pe-tpl-name"
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }} />
          <Button size="sm" onClick={save} disabled={busy} data-testid="pe-tpl-save"
                  className="bg-indigo-700 hover:bg-indigo-800"
                  title="Şu anki metin pozisyonları + foto kutusu + karartmayı taslak olarak kaydet">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <><Save size={11} /> Kaydet</>}
          </Button>
        </div>

        <button type="button" onClick={() => setAiOpen(true)}
                className="w-full flex items-center gap-2 bg-gradient-to-r from-indigo-600 via-fuchsia-600 to-rose-500 text-white rounded-lg px-3 py-2 hover:shadow-lg hover:scale-[1.01] transition font-semibold"
                data-testid="pe-tpl-ai-open">
          <span className="text-base" aria-hidden>🤖</span>
          <span className="flex-1 text-left text-xs">
            AI ile yeni taslaklar üret
            <span className="block text-[10px] font-normal opacity-90">Brief yaz, 1–5 varyant otomatik gelsin</span>
          </span>
          <span className="text-xs">✨</span>
        </button>

        {templates.length === 0 ? (
          <p className="text-[11px] text-stone-500 italic text-center py-3 bg-stone-50 rounded border border-dashed">
            Henüz {platform.label} taslağı yok. Düzeni ayarlayıp ad ver ve kaydet.
          </p>
        ) : (
          <ul className="space-y-1" data-testid="pe-tpl-list">
            {templates.map((tpl) => {
              const isLast = tpl.id === lastTplId;
              return (
                <li key={tpl.id}
                    className={`flex items-center gap-1 p-1.5 border rounded-lg transition ${
                      isLast ? "bg-emerald-50 border-emerald-300" : "bg-stone-50 border-stone-200 hover:bg-indigo-50"
                    }`}>
                  <Layout size={12} className={isLast ? "text-emerald-700 shrink-0" : "text-indigo-600 shrink-0"} />
                  <span className="flex-1 text-xs truncate font-medium">{tpl.name}</span>
                  {isLast && <span className="text-[9px] uppercase bg-emerald-600 text-white rounded px-1 font-bold">AKTİF</span>}
                  <span className="text-[9px] uppercase bg-stone-200 text-stone-700 rounded px-1">
                    {(tpl.output_format || "png").toUpperCase()}
                  </span>
                  <button onClick={() => apply(tpl)} title="Uygula"
                          className="p-1 rounded hover:bg-emerald-100 text-emerald-700"
                          data-testid={`pe-tpl-apply-${tpl.id}`}>
                    <ChevronRight size={12} />
                  </button>
                  <button onClick={() => overwrite(tpl)} title="Mevcut düzenle güncelle"
                          className="p-1 rounded hover:bg-indigo-100 text-indigo-700"
                          data-testid={`pe-tpl-overwrite-${tpl.id}`}>
                    <Save size={11} />
                  </button>
                  <button onClick={() => remove(tpl)} title="Sil"
                          className="p-1 rounded hover:bg-red-50 text-red-600"
                          data-testid={`pe-tpl-del-${tpl.id}`}>
                    <Trash2 size={11} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AiTemplateDialog
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        platformId={platformId}
        platform={platform}
        onSaved={() => { invalidate(); }}
      />
    </section>
  );
}

// --------------------------------------------------------------------------
// Main editor
// --------------------------------------------------------------------------
export default function PostEditor() {
  const qc = useQueryClient();
  const [platformId, setPlatformId] = useState("site");
  const [categoryId, setCategoryId] = useState("");
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [bgId, setBgId] = useState(null);
  const [photoId, setPhotoId] = useState(null);
  const [photoBox, setPhotoBox] = useState(DEFAULT_PHOTO);
  const [textLayers, setTextLayers] = useState(INITIAL_LAYERS);
  const [activeLayerIds, setActiveLayerIds] = useState([]);
  const [guides, setGuides] = useState({ v: null, h: null });
  const activeLayerId = activeLayerIds[0] || null;
  const setActiveLayerId = (id) => setActiveLayerIds(id ? [id] : []);
  const toggleSelect = (id, shift = false) => {
    if (!shift) { setActiveLayerIds(id ? [id] : []); return; }
    setActiveLayerIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const [outputFormat, setOutputFormat] = useState("png");
  const [overlay, setOverlay] = useState(0.35);
  const canvasRef = useRef(null);
  const [textGenOpen, setTextGenOpen] = useState(false);
  const [sourceWritingId, setSourceWritingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastAsset, setLastAsset] = useState(null);
  const [studioMode, setStudioMode] = useState(false);
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [editingLayerId, setEditingLayerId] = useState(null);

  // Studio text tool — "T" aktifken tuval boşluğuna tek tık yeni metin
  // katmanı oluşturur ve o katmana direkt yazma modunu açar. Figma/Canva
  // "metin aracı" davranışı: koy, yaz, Esc/tıkla-çık.
  const [textToolActive, setTextToolActive] = useState(false);

  // --- Undo / Redo history (son 20 state) -----------------------------------
  const histRef = useRef({ stack: [], idx: -1 });
  const applyingHistoryRef = useRef(false);
  const snapshot = () => JSON.stringify({
    textLayers, photoBox, overlay, bgId, photoId, outputFormat,
  });
  const pushHistory = () => {
    if (applyingHistoryRef.current) return;
    const snap = snapshot();
    const h = histRef.current;
    if (h.idx >= 0 && h.stack[h.idx] === snap) return;
    h.stack.splice(h.idx + 1); // dalı buda
    h.stack.push(snap);
    if (h.stack.length > 20) h.stack.shift();
    h.idx = h.stack.length - 1;
  };
  const restoreHistory = (step) => {
    const h = histRef.current;
    const next = h.idx + step;
    if (next < 0 || next >= h.stack.length) return;
    try {
      const data = JSON.parse(h.stack[next]);
      applyingHistoryRef.current = true;
      setTextLayers(data.textLayers || []);
      setPhotoBox(data.photoBox || DEFAULT_PHOTO);
      setOverlay(data.overlay ?? 0.35);
      setBgId(data.bgId || null);
      setPhotoId(data.photoId || null);
      setOutputFormat(data.outputFormat || "png");
      h.idx = next;
      setTimeout(() => { applyingHistoryRef.current = false; }, 50);
      toast.success(step < 0 ? "Geri alındı" : "Yeniden yapıldı", { duration: 900 });
    } catch { applyingHistoryRef.current = false; }
  };
  // Debounced snapshot on tracked state changes
  useEffect(() => {
    const t = setTimeout(pushHistory, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textLayers, photoBox, overlay, bgId, photoId, outputFormat]);
  // Global Cmd/Ctrl+Z keyboard
  useEffect(() => {
    const h = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === "z" || e.key === "Z") {
        if (document.activeElement && ["INPUT","TEXTAREA"].includes(document.activeElement.tagName)) return;
        e.preventDefault();
        restoreHistory(e.shiftKey ? +1 : -1);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Studio modunda ESC ile çıkış
  useEffect(() => {
    if (!studioMode) return;
    const h = (e) => {
      if (e.key !== "Escape") return;
      // Metin düzenleme (inline edit) sırasında ESC Studio'yu kapatmamalı.
      if (editingLayerId) return;
      const t = e.target;
      if (t instanceof Element) {
        const tag = t.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable) return;
      }
      if (!activeLayerIds.length) setStudioMode(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [studioMode, activeLayerIds.length, editingLayerId]);

  // Canvas / toolbar / layer paneli dışına tıklayınca seçimi kaldır.
  useEffect(() => {
    if (!activeLayerIds.length) return;
    const handler = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      // Toolbar, layer panel, canvas, color popover, select menü üzerinde ise koru.
      if (t.closest(
        '[data-pe-interactive="1"],[data-testid="pe-preview-canvas"],[data-testid="pe-inline-toolbar"],[data-testid="pe-layer-panel"],[role="listbox"],[role="dialog"]'
      )) return;
      setActiveLayerIds([]);
      setEditingLayerId(null);
    };
    document.addEventListener("pointerdown", handler, true);
    return () => document.removeEventListener("pointerdown", handler, true);
  }, [activeLayerIds.length]);

  const { data: platforms = [] } = useQuery({
    queryKey: ["pe", "platforms"],
    queryFn: () => api.get("/admin/post-editor/platforms").then((r) => r.data),
    staleTime: Infinity,
  });
  const { data: categories = [], refetch: refetchCategories } = useQuery({
    queryKey: ["pe", "categories"],
    queryFn: () => api.get("/admin/post-editor/categories").then((r) => r.data),
  });

  // Auto-pick the first category on load so the editor isn't empty.
  useEffect(() => {
    if (!categoryId && categories.length) setCategoryId(categories[0].id);
  }, [categories, categoryId]);

  // Reset selected assets when switching categories so a stale bgId
  // (which lived in another category) doesn't fail validation.
  useEffect(() => { setBgId(null); setPhotoId(null); }, [categoryId]);

  // Fotoğraf eklenince / kaldırılınca metin maks. genişliği otomatik yeniden ayarla.
  // Kullanıcı Pro panelden manuel değiştirdiyse (_mwAuto === false) ezme.
  useEffect(() => {
    const target = photoId ? MW_WITH_PHOTO : MW_NO_PHOTO;
    setTextLayers((prev) => prev.map((l) =>
      l._mwAuto === false || l.max_width === target ? l : { ...l, max_width: target }
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoId]);

  const assetParams = categoryId ? { category_id: categoryId } : null;

  const { data: backgrounds = [] } = useQuery({
    queryKey: ["pe", "backgrounds", categoryId],
    queryFn: () => api.get("/admin/post-editor/backgrounds", { params: assetParams }).then((r) => r.data),
    enabled: !!categoryId,
  });
  const { data: photos = [] } = useQuery({
    queryKey: ["pe", "photos", categoryId],
    queryFn: () => api.get("/admin/post-editor/photos", { params: assetParams }).then((r) => r.data),
    enabled: !!categoryId,
  });
  const { data: logos = [] } = useQuery({
    queryKey: ["pe", "logos", categoryId],
    queryFn: () => api.get("/admin/post-editor/logos", { params: assetParams }).then((r) => r.data),
    enabled: !!categoryId,
  });
  const { data: assets = [] } = useQuery({
    queryKey: ["pe", "assets", platformId, categoryId],
    queryFn: () => api.get("/admin/post-editor/assets", {
      params: { platform: platformId, ...(categoryId ? { category_id: categoryId } : {}) },
    }).then((r) => r.data),
    enabled: !!categoryId,
  });

  const platform = useMemo(() => platforms.find((p) => p.id === platformId) || platforms[0],
    [platforms, platformId]);
  const aspect = platform ? platform.w / platform.h : 1;
  const activeCategory = useMemo(() => categories.find((c) => c.id === categoryId) || null,
    [categories, categoryId]);

  const invalidate = (keys) => keys.forEach((k) => qc.invalidateQueries({ queryKey: ["pe", k] }));

  const deleteAsset = async (kind, row) => {
    if (!window.confirm(`"${row.name}" silinecek. Onaylıyor musunuz?`)) return;
    try {
      await api.delete(`/admin/post-editor/${kind}/${row.id}`);
      invalidate([kind, "categories"]);
      toast.success("Silindi");
      if (kind === "backgrounds" && bgId === row.id) setBgId(null);
      if (kind === "photos" && photoId === row.id) setPhotoId(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteComposed = async (a) => {
    if (!window.confirm("Bu kayıtlı post silinsin mi?")) return;
    try {
      await api.delete(`/admin/post-editor/assets/${a.id}`);
      invalidate(["assets"]);
      toast.success("Silindi");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const compose = async () => {
    if (!platform) return;
    if (!categoryId) { toast.error("Önce bir kategori seç"); return; }
    const texts = textLayers
      .filter((l) => (l.text || "").trim() && !l.hidden)
      .map(({ id, hidden, locked, _mwAuto, ...rest }) => rest);

    const titleForMeta = (textLayers.find((l) => (l.text || "").trim()) || {}).text || "";

    const body = {
      platform: platformId,
      category_id: categoryId,
      title: titleForMeta,
      caption,
      hashtags: hashtags.split(/\s+/).map((h) => h.trim()).filter(Boolean),
      source_writing_id: sourceWritingId,
      bg_id: bgId,
      bg_color: "#1e1b4b",
      overlay_alpha: overlay,
      photo_id: photoId,
      photo: photoId ? photoBox : null,
      texts,
      output_format: outputFormat,
    };
    setBusy(true);
    try {
      const { data } = await api.post("/admin/post-editor/assets", body);
      setLastAsset(data);
      invalidate(["assets"]);
      toast.success("Post üretildi");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  // Draft bir post üretildiğinde kullanılabilir hale gelen compose payload'ı.
  // Taslak kaydet butonu `null` dönerse "önce içerik hazırla" uyarısı atar.
  const buildDraftPayload = () => {
    if (!platform || !categoryId) return null;
    const texts = textLayers
      .filter((l) => (l.text || "").trim() && !l.hidden)
      .map(({ id, hidden, locked, _mwAuto, ...rest }) => rest);
    const titleForMeta = (textLayers.find((l) => (l.text || "").trim()) || {}).text || "";
    if (!texts.length && !bgId && !photoId) return null;
    return {
      platform: platformId,
      category_id: categoryId,
      title: titleForMeta,
      caption,
      hashtags: hashtags.split(/\s+/).map((h) => h.trim()).filter(Boolean),
      source_writing_id: sourceWritingId,
      bg_id: bgId,
      bg_color: "#1e1b4b",
      overlay_alpha: overlay,
      photo_id: photoId,
      photo: photoId ? photoBox : null,
      texts,
      output_format: outputFormat,
    };
  };

  // Kayıtlı taslağı editöre uygula: tek tek state alanlarını geri yükler.
  // Metin katmanları `id`/`hidden`/`locked`/`_mwAuto` olmadan saklandığından
  // bunları makul varsayılanlarla doldurur. Compose ile birebir aynı sonucu
  // üretmek için payload'ı değiştirmeden "Hemen Üret" seçeneğini kullan.
  const loadDraftPayload = (p) => {
    if (!p) return;
    if (p.bg_id !== undefined)        setBgId(p.bg_id || null);
    if (p.photo_id !== undefined)     setPhotoId(p.photo_id || null);
    if (p.photo && typeof p.photo === "object") setPhotoBox({ ...DEFAULT_PHOTO, ...p.photo });
    if (p.overlay_alpha !== undefined) setOverlay(Math.max(0, Math.min(1, Number(p.overlay_alpha) || 0.35)));
    if (p.output_format)              setOutputFormat(String(p.output_format).toLowerCase() === "jpeg" ? "jpeg" : "png");
    if (typeof p.caption === "string") setCaption(p.caption);
    if (Array.isArray(p.hashtags))    setHashtags(p.hashtags.join(" "));
    if (Array.isArray(p.texts) && p.texts.length) {
      const layers = p.texts.map((t, i) => ({
        id: makeLayerId(),
        hidden: false,
        locked: false,
        _mwAuto: false,
        text: "",
        ...autoLayout(i, !!p.photo_id),
        ...t,
      }));
      setTextLayers(layers);
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Kopyalandı"); };

  if (!platform) return (
    <div className="p-10 text-center text-stone-400 italic flex items-center justify-center gap-2">
      <Loader2 size={16} className="animate-spin" /> Platform listesi yükleniyor...
    </div>
  );

  // Logo auto-selected: the first logo of the active category
  const activeLogo = logos[logos.length - 1] || logos[0] || null;

  return (
    <div data-testid="post-editor" className={studioMode
      ? "fixed inset-0 z-40 bg-gradient-to-br from-slate-100 to-indigo-50 overflow-auto p-3"
      : ""}>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
               style={{ background: "linear-gradient(135deg,#f97316 0%,#db2777 100%)" }}>
            <Palette size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-slate-900">
              Post Editörü {studioMode && <span className="text-sm font-semibold text-indigo-700 ml-1">· Stüdyo Modu</span>}
            </h1>
            <p className="text-xs text-stone-500">
              Önce bir <b>kategori</b> oluştur. İçine arka plan, foto ve logolarını yükle; sonra platforma göre tasarla.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-stone-200 rounded-lg shadow-sm p-1">
            <button type="button" onClick={() => restoreHistory(-1)}
                    className="w-8 h-8 rounded hover:bg-stone-100 text-slate-700 text-sm"
                    title="Geri al (Ctrl+Z)" data-testid="pe-undo">↶</button>
            <button type="button" onClick={() => restoreHistory(+1)}
                    className="w-8 h-8 rounded hover:bg-stone-100 text-slate-700 text-sm"
                    title="Yeniden yap (Ctrl+Shift+Z)" data-testid="pe-redo">↷</button>
          </div>
          <button type="button" onClick={() => setStudioMode((v) => !v)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm shadow-lg transition hover:scale-[1.02] ${
                    studioMode
                      ? "bg-white border-2 border-indigo-300 text-indigo-900"
                      : "bg-gradient-to-r from-indigo-600 via-fuchsia-600 to-rose-500 text-white"
                  }`}
                  data-testid="pe-studio-toggle">
            {studioMode
              ? <>✕ Stüdyo Modunu Kapat <span className="text-[10px] opacity-60 ml-1 font-normal">(ESC)</span></>
              : <>⚡ STÜDYO MODUNU AÇ <span className="text-[10px] opacity-80 ml-1 font-normal">(Tam Ekran Tasarım)</span></>
            }
          </button>
        </div>
      </div>

      {/* Platform tabs */}
      <div className="flex gap-1.5 flex-wrap mb-4" data-testid="pe-platform-tabs">
        {platforms.map((p) => (
          <button key={p.id} onClick={() => setPlatformId(p.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    p.id === platformId ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white border-stone-200 text-stone-700 hover:border-slate-400"
                  }`} data-testid={`pe-platform-${p.id}`}>
            {p.label}<span className="opacity-60 ml-1 font-normal">· {p.w}×{p.h}</span>
          </button>
        ))}
      </div>

      <div className={studioMode
        ? "grid grid-cols-1 xl:grid-cols-[180px_210px_minmax(0,1fr)_300px] gap-2 h-[calc(100vh-140px)]"
        : "grid grid-cols-1 xl:grid-cols-[210px_240px_minmax(0,1fr)_300px] gap-3"}>
        {/* === Column 1: Categories === */}
        <CategorySidebar categories={categories} activeId={categoryId}
                         onPick={setCategoryId} onRefresh={refetchCategories} />

        {/* === Column 2: Asset libraries (scoped to active category) === */}
        <div className="space-y-3 min-w-0">
          {!categoryId ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
              <FolderTree size={28} className="mx-auto text-amber-500 mb-2" />
              <p className="text-sm font-semibold text-amber-900">Önce bir kategori seç</p>
              <p className="text-xs text-amber-700 mt-1">Soldaki panelden kategori oluştur ya da seç; arka plan, foto ve logolar burada o kategoriye özel listelenecek.</p>
            </div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold flex items-center gap-1">
                <FolderOpen size={11} /> {activeCategory?.name} · Varlıklar
              </div>
              <section className="bg-white rounded-xl border border-indigo-100 shadow-sm p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <ImageIcon size={14} className="text-indigo-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-900 flex-1">Arka Planlar</h3>
                </div>
                <UploadTile kind="backgrounds" categoryId={categoryId} label="Arka plan yükle"
                            onDone={() => invalidate(["backgrounds", "categories"])} />
                <div className="mt-2">
                  <AssetGrid rows={backgrounds} selectedId={bgId} testid="pe-bg-grid"
                             onPick={(r) => setBgId(bgId === r.id ? null : r.id)}
                             onDelete={(r) => deleteAsset("backgrounds", r)} kind="backgrounds" />
                </div>
              </section>

              <section className="bg-white rounded-xl border border-indigo-100 shadow-sm p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <User size={14} className="text-indigo-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-900 flex-1">Fotoğraflar</h3>
                </div>
                <UploadTile kind="photos" categoryId={categoryId} label="Fotoğraf yükle"
                            onDone={() => invalidate(["photos", "categories"])} />
                <div className="mt-2">
                  <AssetGrid rows={photos} selectedId={photoId} testid="pe-photo-grid"
                             onPick={(r) => setPhotoId(photoId === r.id ? null : r.id)}
                             onDelete={(r) => deleteAsset("photos", r)} kind="photos" />
                </div>
              </section>

              <section className="bg-white rounded-xl border border-indigo-100 shadow-sm p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Sparkles size={14} className="text-indigo-600" />
                  <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-900 flex-1">Logolar</h3>
                </div>
                <UploadTile kind="logos" categoryId={categoryId} label="Logo yükle"
                            extra={{ position: "bottom-right", opacity: 0.9 }}
                            onDone={() => invalidate(["logos", "categories"])} />
                <ul className="mt-2 space-y-1" data-testid="pe-logo-list">
                  {logos.length === 0 && (
                    <li className="text-[11px] text-stone-400 italic text-center py-2">Logo yok.</li>
                  )}
                  {logos.map((l) => (
                    <li key={l.id} className="flex items-center gap-2 p-1.5 border rounded-lg bg-stone-50 hover:bg-indigo-50 transition">
                      <img src={absUrl(l.url)} alt={l.name} className="w-9 h-9 object-contain bg-white rounded border" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold truncate">{l.name}</p>
                        <p className="text-[10px] text-stone-500 truncate">
                          {LOGO_POSITIONS.find(([k]) => k === l.position)?.[1] || l.position}
                        </p>
                      </div>
                      <button onClick={() => deleteAsset("logos", l)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-stone-400 mt-2">
                  Logolar bu kategoriye özeldir; üretim sırasında otomatik eklenir.
                </p>
              </section>

              <TemplatesCard
                platformId={platformId}
                platform={platform}
                categoryId={categoryId}
                getCurrent={() => ({
                  overlay_alpha: overlay,
                  photo: photoBox,
                  texts: textLayers.map(({ id, ...rest }) => rest),
                  output_format: outputFormat,
                })}
                applyTemplate={(tpl) => {
                  setOverlay(tpl.overlay_alpha ?? 0.35);
                  if (tpl.photo && tpl.photo.x != null) setPhotoBox({ ...DEFAULT_PHOTO, ...tpl.photo });
                  const incoming = Array.isArray(tpl.texts) ? tpl.texts : [];
                  const existingTexts = textLayers.map((l) => l.text).filter(Boolean);
                  // Preset slot sayısından çok metin varsa, son slot stilini tekrarlayıp
                  // altına ek satır(lar) ekle — kullanıcının metni kaybolmasın.
                  const total = Math.max(incoming.length, existingTexts.length);
                  const next = [];
                  for (let i = 0; i < total; i++) {
                    const slot = incoming[i] || incoming[incoming.length - 1] || {};
                    const text = (i < incoming.length ? slot.text : "") || existingTexts[i] || "";
                    if (i < incoming.length) {
                      next.push(freshLayer({ ...slot, text }, i));
                    } else {
                      const prev = next[next.length - 1] || {};
                      const extraY = Math.min(0.92, (prev.y || 0.1) + (prev.size || 0.07) * AUTO_LINE_FACTOR);
                      next.push(freshLayer({ ...slot, text, y: extraY }, i));
                    }
                  }
                  if (next.length === 0) next.push(freshLayer({}, 0));
                  setTextLayers(next);
                  setActiveLayerId(next[0].id);
                  setOutputFormat(tpl.output_format || "png");
                  toast.success(`"${tpl.name}" şablonu uygulandı`);
                }}
              />
            </>
          )}
        </div>

        {/* === Column 3: Canvas + inputs ===
            Studio modda hem canvas kartı hem compose kartı viewport'a sticky
            olarak pinleniyor. Canvas top-2'de, compose onun altında (top-[57vh])
            durur. Böylece scroll yaparken HEM önizleme HEM Metin Katmanları +
            Üret butonu her zaman görünür ve metin ekleme alanı canvas'ın
            üzerine KAYMAZ. */}
        <div className="space-y-3 min-w-0">
          <div className={`bg-white rounded-xl border border-indigo-100 shadow-sm p-3 ${
            studioMode ? "sticky top-2 z-20" : ""
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-900 flex-1">
                Canlı Önizleme — {platform.label}
              </span>
              {studioMode && (
                <>
                  <button
                    type="button"
                    onClick={() => setTextToolActive((v) => !v)}
                    className={`inline-flex items-center gap-1 h-6 px-2 rounded text-[10px] font-bold border transition ${
                      textToolActive
                        ? "bg-indigo-600 text-white border-indigo-600 shadow"
                        : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                    }`}
                    data-testid="pe-text-tool"
                    title="Metin aracı · tuvalde bir yere tıkla, yaz, Esc ile çık">
                    <span style={{ fontFamily: "Playfair Display, serif", fontSize: 13 }}>T</span>
                    {textToolActive ? "Metin aracı aktif" : "Metin ekle"}
                  </button>
                  <div className="flex items-center gap-1 text-[10px] text-stone-500">
                    <button onClick={() => setCanvasZoom((z) => Math.max(0.3, z - 0.1))}
                            className="w-5 h-5 rounded hover:bg-stone-100" data-testid="pe-zoom-out">−</button>
                    <span className="w-10 text-center font-mono">{Math.round(canvasZoom * 100)}%</span>
                    <button onClick={() => setCanvasZoom((z) => Math.min(2, z + 0.1))}
                            className="w-5 h-5 rounded hover:bg-stone-100" data-testid="pe-zoom-in">+</button>
                    <button onClick={() => setCanvasZoom(1)}
                            className="text-indigo-600 hover:underline ml-1" data-testid="pe-zoom-reset">1:1</button>
                  </div>
                </>
              )}
              <span className="text-[10px] text-stone-400">{platform.w}×{platform.h}</span>
            </div>
            <div ref={canvasRef}
                 className="relative mx-auto rounded-lg overflow-hidden shadow-inner bg-gradient-to-br from-slate-900 to-indigo-900 select-none"
                 style={(() => {
                   // Studio modda canvas 55vh ile sınırlı → altında kalan
                   // ~45vh'de compose kartı sticky duruyor. Non-studio modda
                   // 560px cap kalıyor.
                   const maxH = studioMode ? "55vh" : "560px";
                   return {
                     width: `min(${canvasZoom * 100}%, calc(${maxH} * ${aspect}))`,
                     aspectRatio: aspect,
                     maxHeight: maxH,
                     containerType: "size",
                     cursor: textToolActive ? "text" : "default",
                   };
                 })()}
                 onPointerDown={(e) => {
                   if (e.target !== e.currentTarget) return;
                   if (textToolActive) {
                     // Yeni metin katmanı tıklanan konumda oluşsun.
                     const rect = e.currentTarget.getBoundingClientRect();
                     const nx = Math.max(0.02, Math.min(0.95, (e.clientX - rect.left) / rect.width));
                     const ny = Math.max(0.02, Math.min(0.95, (e.clientY - rect.top) / rect.height));
                     const idx = textLayers.length;
                     const nl = freshLayer({ text: "Metin", x: nx, y: ny, align: "left", _mwAuto: false, max_width: 0.6 }, idx, !!photoId);
                     setTextLayers((prev) => [...prev, nl]);
                     setActiveLayerIds([nl.id]);
                     setEditingLayerId(nl.id);
                     setTextToolActive(false);
                     return;
                   }
                   setActiveLayerIds([]);
                   setEditingLayerId(null);
                 }}
                 tabIndex={0}
                 onKeyDown={(e) => {
                   if (textToolActive && e.key === "Escape") { setTextToolActive(false); return; }
                   if (activeLayerIds.length === 0) return;
                   const nudge = e.shiftKey ? 0.02 : 0.004;
                   let dx = 0, dy = 0;
                   if (e.key === "ArrowLeft")  dx = -nudge;
                   else if (e.key === "ArrowRight") dx =  nudge;
                   else if (e.key === "ArrowUp")    dy = -nudge;
                   else if (e.key === "ArrowDown")  dy =  nudge;
                   else if (e.key === "Delete" || e.key === "Backspace") {
                     e.preventDefault();
                     const ids = new Set(activeLayerIds);
                     setTextLayers((prev) => {
                       const next = prev.filter((l) => !ids.has(l.id));
                       return next.length ? next : [freshLayer({}, 0)];
                     });
                     setActiveLayerIds([]);
                     return;
                   } else if (e.key === "Escape") { setActiveLayerIds([]); return; }
                   else return;
                   e.preventDefault();
                   const sel = new Set(activeLayerIds);
                   setTextLayers((prev) => prev.map((l) => sel.has(l.id)
                     ? { ...l, x: Math.max(0, Math.min(1, l.x + dx)),
                               y: Math.max(0, Math.min(1, l.y + dy)) }
                     : l));
                 }}
                 data-testid="pe-preview-canvas">
              {bgId && (() => {
                const bg = backgrounds.find((b) => b.id === bgId);
                return bg ? <img src={absUrl(bg.url)} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" /> : null;
              })()}
              <div className="absolute inset-0 bg-black pointer-events-none" style={{ opacity: overlay }} />
              {photoId && (() => {
                const p = photos.find((x) => x.id === photoId);
                return p ? (
                  <DraggablePhoto url={absUrl(p.url)} box={photoBox} onChange={setPhotoBox}
                                  shape={photoBox.shape} canvasRef={canvasRef} />
                ) : null;
              })()}
              {textLayers.map((layer) => layer.text && !layer.hidden ? (
                <DraggableTextLayer key={layer.id}
                                    layer={layer}
                                    active={activeLayerIds.includes(layer.id)}
                                    selectedIds={activeLayerIds}
                                    allLayers={textLayers}
                                    canvasRef={canvasRef}
                                    onSelect={toggleSelect}
                                    onGuides={setGuides}
                                    editing={editingLayerId === layer.id}
                                    onEditStart={(id) => setEditingLayerId(id)}
                                    onEditEnd={() => setEditingLayerId(null)}
                                    onChange={(id, patch) =>
                                      setTextLayers((prev) => prev.map((l) => l.id === id ? { ...l, ...patch } : l))
                                    }
                                    onMulti={(ids, dxN, dyN, startMap) =>
                                      setTextLayers((prev) => prev.map((l) => {
                                        if (!ids.includes(l.id) || !startMap[l.id] || l.locked) return l;
                                        const s = startMap[l.id];
                                        return { ...l,
                                          x: Math.max(0, Math.min(1, s.x + dxN)),
                                          y: Math.max(0, Math.min(1, s.y + dyN)) };
                                      }))
                                    } />
              ) : null)}
              {/* Smart guides */}
              {guides.v !== null && (
                <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-pink-500 pointer-events-none"
                     style={{ left: `${guides.v * 100}%` }}
                     data-testid="pe-guide-v" />
              )}
              {guides.h !== null && (
                <div className="absolute left-0 right-0 border-t-2 border-dashed border-pink-500 pointer-events-none"
                     style={{ top: `${guides.h * 100}%` }}
                     data-testid="pe-guide-h" />
              )}
              {activeLayerIds.length === 1 && (() => {
                const active = textLayers.find((l) => l.id === activeLayerId && l.text);
                if (!active) return null;
                return (
                  <InlineToolbar layer={active}
                                 allLayers={textLayers}
                                 canvasRef={canvasRef}
                                 onChange={(id, patch) =>
                                   setTextLayers((prev) => prev.map((l) => l.id === id ? { ...l, ...patch } : l))
                                 }
                                 onDelete={(id) => {
                                   setTextLayers((prev) => {
                                     const next = prev.filter((l) => l.id !== id);
                                     return next.length ? next : [freshLayer({}, 0)];
                                   });
                                   setActiveLayerIds([]);
                                 }} />
                );
              })()}
              {activeLayerIds.length > 1 && (
                <>
                  <div className="absolute left-2 top-2 bg-amber-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold shadow"
                       data-testid="pe-multi-badge">
                    {activeLayerIds.length} katman seçili · Shift+tıkla ekle/çıkar
                  </div>
                  <AlignToolbar selectedIds={activeLayerIds} setLayers={setTextLayers} canvasRef={canvasRef} />
                </>
              )}
              {activeLogo && (() => {
                const posMap = {
                  "top-left":     { top: "3%",  left: "3%"  },
                  "top-right":    { top: "3%",  right: "3%" },
                  "bottom-left":  { bottom: "3%", left: "3%" },
                  "bottom-right": { bottom: "3%", right: "3%" },
                  "center":       { top: "50%", left: "50%", transform: "translate(-50%,-50%)" },
                };
                return (
                  <img src={absUrl(activeLogo.url)} alt=""
                       className="absolute object-contain pointer-events-none"
                       style={{ width: "18%", opacity: activeLogo.opacity || 0.9, ...posMap[activeLogo.position || "bottom-right"] }} />
                );
              })()}
            </div>
            <p className="text-[10px] text-stone-400 mt-1 text-center">
              Önizleme yaklaşık — Üret'e bastığında sunucuda tam çözünürlükte oluşturulur.
            </p>
          </div>

          <div className={`bg-white rounded-xl border border-indigo-100 shadow-sm p-3 space-y-3 ${
            studioMode ? "sticky top-[calc(55vh+80px)] z-10 max-h-[calc(45vh-100px)] overflow-y-auto" : ""
          }`}>
            <div>
              <label className="text-[11px] font-semibold text-stone-600 block mb-1">
                Karartma şiddeti ({Math.round(overlay * 100)}%)
              </label>
              <input type="range" min="0" max="0.75" step="0.05" value={overlay}
                     onChange={(e) => setOverlay(parseFloat(e.target.value))}
                     className="w-full" data-testid="pe-overlay" />
            </div>

            <TextLayersEditor
              layers={textLayers}
              setLayers={setTextLayers}
              activeId={activeLayerId}
              setActiveId={setActiveLayerId}
              hasPhoto={!!photoId}
              studioOnly={!studioMode}
            />

            {photoId && (
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 text-[11px] text-stone-600 mb-1">
                  <span className="font-semibold text-stone-800 flex-1">Fotoğraf</span>
                  <select value={photoBox.shape || "rect"}
                          onChange={(e) => setPhotoBox({ ...photoBox, shape: e.target.value })}
                          className="h-6 text-[11px] border rounded px-1 bg-white"
                          data-testid="pe-photo-shape">
                    <option value="rect">◼ Dikdörtgen</option>
                    <option value="circle">⬤ Daire</option>
                  </select>
                  <button type="button" onClick={() => setPhotoBox(DEFAULT_PHOTO)}
                          className="text-[10px] text-indigo-700 hover:underline"
                          data-testid="pe-photo-reset">
                    Sıfırla
                  </button>
                </div>
                <p className="text-[10px] text-stone-500">
                  İpucu: önizlemede fotoğrafı sürükle, köşe tutamaklarıyla yeniden boyutlandır. Shift basılı tut → oranı koru.
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <span className="text-[11px] font-semibold text-stone-600 shrink-0">Çıktı:</span>
              {[["png", "PNG"], ["jpeg", "JPEG"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setOutputFormat(v)}
                        className={`px-2.5 py-1 rounded-full border text-[11px] font-semibold ${
                          outputFormat === v ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-stone-600 border-stone-200 hover:border-slate-400"
                        }`} data-testid={`pe-format-${v}`}>
                  {l}
                </button>
              ))}
              <span className="text-[10px] text-stone-400 ml-auto">
                {outputFormat === "png" ? "şeffaf" : "küçük boyut"}
              </span>
            </div>
            <Button onClick={compose} disabled={busy || !categoryId} className="w-full h-11" data-testid="pe-compose">
              {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
              {platform.label} · {outputFormat.toUpperCase()} olarak kaydet
            </Button>
          </div>
        </div>

        {/* === Column 4: Caption + hashtags + library === */}
        <div className="space-y-3 min-w-0">
          {studioMode && (
            <LayerPanel layers={textLayers} selectedIds={activeLayerIds}
                        onSelect={(id, shift) => toggleSelect(id, shift)}
                        onToggleVis={(id) => setTextLayers((p) => p.map((l) => l.id === id ? { ...l, hidden: !l.hidden } : l))}
                        onToggleLock={(id) => setTextLayers((p) => p.map((l) => l.id === id ? { ...l, locked: !l.locked } : l))}
                        onMove={(id, dir) => setTextLayers((prev) => {
                          const idx = prev.findIndex((l) => l.id === id);
                          const to = idx + dir;
                          if (idx < 0 || to < 0 || to >= prev.length) return prev;
                          const next = prev.slice();
                          [next[idx], next[to]] = [next[to], next[idx]];
                          return next;
                        })}
                        onDelete={(id) => {
                          setTextLayers((prev) => {
                            const n = prev.filter((l) => l.id !== id);
                            return n.length ? n : [freshLayer({}, 0)];
                          });
                          setActiveLayerIds((prev) => prev.filter((x) => x !== id));
                        }} />
          )}

          <div className="bg-white rounded-xl border border-indigo-100 shadow-sm p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-indigo-900 flex-1">Metin & Hashtag</span>
              <Button size="sm" variant="outline" onClick={() => setTextGenOpen(true)} data-testid="pe-text-open">
                <Wand2 size={11} className="mr-1" />AI üret
              </Button>
            </div>
            <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={5}
                      placeholder="Post / caption metni..." className="text-xs" data-testid="pe-caption" />
            <div className="relative mt-2">
              <Hash size={11} className="absolute left-2.5 top-2.5 text-stone-400" />
              <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)}
                     placeholder="#tag1 #tag2 ..." className="pl-7 text-xs" data-testid="pe-hashtags" />
            </div>
            {(caption || hashtags) && (
              <div className="flex gap-1 mt-2">
                <Button size="sm" variant="outline" onClick={() => copy([caption, hashtags].filter(Boolean).join("\n\n"))}
                        className="text-[11px] flex-1">
                  <Copy size={11} className="mr-1" />Metni kopyala
                </Button>
              </div>
            )}
          </div>

          <DraftsCard platformId={platformId}
                      platformLabel={platform.label}
                      categoryId={categoryId}
                      buildPayload={buildDraftPayload}
                      onLoad={loadDraftPayload}
                      onProduced={(a) => setLastAsset(a)} />

          <div className="bg-white rounded-xl border border-indigo-100 shadow-sm p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-indigo-900 flex-1 leading-tight">
                Kütüphanem · {platform.label}
              </span>
              <span className="text-[10px] text-stone-400 shrink-0">{assets.length}</span>
            </div>
            {assets.length === 0 ? (
              <p className="text-xs text-stone-400 italic text-center py-6">Henüz üretilmiş post yok.</p>
            ) : (
              <ul className="space-y-2 max-h-[520px] overflow-y-auto" data-testid="pe-library">
                {assets.map((a) => (
                  <li key={a.id} className="border rounded-lg overflow-hidden group bg-stone-50 relative"
                      data-testid={`pe-library-item-${a.id}`}>
                    <img src={absUrl(a.url)} alt={a.title} className="w-full object-cover" />
                    <div className="p-2">
                      <p className="text-xs font-semibold text-slate-900 line-clamp-1">{a.title || "(başlıksız)"}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {a.category && <span className="text-[10px] bg-indigo-100 text-indigo-800 rounded-full px-1.5 py-0.5">{a.category}</span>}
                        <span className="flex-1" />
                        <a href={`${absUrl(a.url)}?dl=1`}
                           className="p-1 rounded hover:bg-indigo-50 text-indigo-700" title="İndir" download={a.title ? `${a.title}.${(a.url.split('.').pop() || 'png').split('?')[0]}` : true}>
                          <Download size={11} />
                        </a>
                        <button onClick={() => deleteComposed(a)}
                                className="p-1 rounded hover:bg-red-50 text-red-600" title="Sil">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {lastAsset && (
        <div className="fixed bottom-4 right-4 bg-white border border-emerald-200 shadow-xl rounded-xl p-3 flex items-center gap-3 max-w-sm z-40"
             data-testid="pe-last-asset">
          <img src={absUrl(lastAsset.url)} alt="" className="w-14 h-14 object-cover rounded" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-emerald-900">Hazır!</p>
            <p className="text-[11px] text-stone-500 truncate">{lastAsset.title || "Yeni post"}</p>
            <div className="flex gap-1 mt-1">
              <a href={`${absUrl(lastAsset.url)}?dl=1`} download={lastAsset.title ? `${lastAsset.title}.${(lastAsset.url.split('.').pop() || 'png').split('?')[0]}` : true}
                 className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-1">
                <Download size={10} />İndir
              </a>
              <button onClick={() => setLastAsset(null)}
                      className="text-[11px] text-stone-500 hover:underline">Kapat</button>
            </div>
          </div>
        </div>
      )}

      <TextGenModal open={textGenOpen} onClose={() => setTextGenOpen(false)}
                    platform={platformId}
                    onApply={({ caption: c, hashtags: hs }) => {
                      setCaption(c);
                      setHashtags(hs.join(" "));
                      toast.success("Metin kompozisyona eklendi");
                    }} />
    </div>
  );
}
